import Check from "@mui/icons-material/Check";
import Close from "@mui/icons-material/Close";
import { Stack, Tooltip, Typography } from "@mui/material";
import { GridColDef } from "@mui/x-data-grid-premium";
import { SparkleIcon } from "components/ArtificialIntelligence/SparkleIcon";
import { HoverAction } from "components/HoverAction";
import { useMemo } from "react";

import type { SuggestedFacility } from "../types";

import { DocumentFacilitiesGrid } from "./DocumentFacilitiesGrid";

function formatAddress({ streetAddress1, city, state, zip }: SuggestedFacility): string {
  return [streetAddress1, city, state, zip].filter(Boolean).join(", ");
}

type SuggestedFacilitiesGridProps = {
  facilities: SuggestedFacility[];
  onAddFacility?: (facility: SuggestedFacility) => void;
  onRejectFacility?: (facilityId: string) => void;
  loading?: boolean;
};

export function SuggestedFacilitiesGrid({
  facilities,
  onAddFacility,
  onRejectFacility,
  loading = false,
}: SuggestedFacilitiesGridProps) {
  const additionalActionColumns = useMemo<GridColDef<SuggestedFacility>[]>(
    () => [
      {
        field: "addReject",
        type: "actions",
        sortable: false,
        filterable: false,
        flex: 0.5,
        getActions: ({ row }) => {
          const actions = [];
          if (onAddFacility) {
            actions.push(
              <Tooltip title="Accept" key="accept">
                <HoverAction
                  label="Accept suggestion"
                  icon={<Check fontSize="small" color="primary" />}
                  onClick={() => onAddFacility(row)}
                />
              </Tooltip>
            );
          }

          if (onRejectFacility) {
            actions.push(
              <Tooltip title="Clear" key="reject">
                <HoverAction
                  label="Clear suggestion"
                  icon={<Close fontSize="small" />}
                  onClick={() => onRejectFacility(row.id)}
                />
              </Tooltip>
            );
          }

          return actions;
        },
      },
    ],
    [onAddFacility, onRejectFacility]
  );

  return (
    <DocumentFacilitiesGrid
      facilities={facilities}
      loading={loading}
      noRowsMessage="No suggested facilities."
      hideHeader={true}
      nameCellRenderer={(row) => (
        <Stack direction="row" spacing={1} sx={{ width: "100%" }} alignItems="center">
          <Tooltip title={row.name}>
            <Typography
              variant="body2"
              color="text.secondary"
              noWrap
              style={{ overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {row.name}
            </Typography>
          </Tooltip>
        </Stack>
      )}
      addressCellRenderer={(row) => (
        <Tooltip title={formatAddress(row)}>
          <Typography
            variant="body2"
            color="text.secondary"
            noWrap
            style={{ overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {formatAddress(row)}
          </Typography>
        </Tooltip>
      )}
      additionalActionColumns={additionalActionColumns}
    />
  );
}
